import { useEffect, useMemo, useState } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ehDataIso } from '@malgoos/shared';
import type { Agendamento, Convidado, Frequencia, TipoReuniao } from '../modules/agenda/tipos';
import { get } from '../nucleo/api';
import { data as fmtData } from '../nucleo/formato';
import { useSessao } from '../nucleo/sessao';
import { CaixaMarcar, CHAVE, diaLocal } from './agendamento-comum';
import { CampoConvidados, convidadosParaApi, RE_EMAIL } from './convidados';
import { ariaErro } from './validacao-local';
import { CampoData, EntradaSensivel, Rotulo, Selecao } from './formulario';
import { hojeNoFuso } from './periodo/periodo';

/**
 * Reunião livre (ficha 2026-11-03): os campos do formulário igual ao do Google Agenda (horário ou dia inteiro, repetição,
 * convidados, local e Google Meet), o estado deles e o corpo para a API. Ficam aqui porque a Agenda e a tarefa do CRM usam os
 * mesmos (tela de módulo não importa tela de outro módulo). Título e descrição são de quem usa: na tarefa, são os da tarefa.
 * Horas pelo relógio do navegador, como o resto da Agenda; o dia inteiro vai como data e o servidor resolve no fuso do workspace.
 */

export const REPETICOES: Frequencia[] = ['diaria', 'semanal', 'quinzenal', 'mensal'];
const UMA_HORA_MS = 3_600_000;
const UM_DIA_MS = 86_400_000;
const RE_HORA = /^\d{2}:\d{2}$/;

export interface EstadoReuniaoLivre {
  diaInteiro: boolean;
  /** Dia da reunião; no dia inteiro, o primeiro dia. */
  dia: string;
  /** Último dia, inclusivo (dia inteiro). */
  diaFinal: string;
  horaInicio: string;
  horaFim: string;
  /** Dias entre `dia` e o dia do fim (reunião que passa da meia-noite ou gravada com mais de um dia). */
  deslocFim: number;
  /** Duração que mudar o início mantém. */
  duracaoMs: number;
  frequencia: Frequencia | '';
  ate: string;
  convidados: Convidado[];
  /** E-mail digitado e ainda não incluído: vai junto ao salvar. */
  rascunhoConvidado: { email: string; nome: string } | null;
  local: string;
  meet: boolean;
}

const dois = (n: number) => String(n).padStart(2, '0');
const horaLocal = (d: Date) => `${dois(d.getHours())}:${dois(d.getMinutes())}`;

/** Soma dias a um AAAA-MM-DD, sem passar por UTC. */
export function somarDiasIso(dia: string, n: number): string {
  const [a, m, d] = dia.split('-').map(Number) as [number, number, number];
  return diaLocal(new Date(a, m - 1, d + n));
}
const diasEntre = (de: string, ate: string) => Math.round((Date.parse(`${ate}T12:00:00Z`) - Date.parse(`${de}T12:00:00Z`)) / UM_DIA_MS);

function instante(dia: string, hora: string): Date | null {
  if (!ehDataIso(dia) || !RE_HORA.test(hora)) return null;
  const d = new Date(`${dia}T${hora}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const inicioDoEstado = (e: Pick<EstadoReuniaoLivre, 'dia' | 'horaInicio'>) => instante(e.dia, e.horaInicio);

/** Fim da reunião com hora. Fim antes do início, no mesmo dia, é no dia seguinte (como no Google); igual ao início é inválido. */
export function fimDoEstado(e: Pick<EstadoReuniaoLivre, 'dia' | 'horaInicio' | 'horaFim' | 'deslocFim'>): Date | null {
  const ini = inicioDoEstado(e);
  if (!ini || !ehDataIso(e.dia)) return null;
  const fim = instante(somarDiasIso(e.dia, e.deslocFim), e.horaFim);
  if (!fim) return null;
  if (fim > ini) return fim;
  if (e.deslocFim === 0 && fim < ini) return new Date(fim.getFullYear(), fim.getMonth(), fim.getDate() + 1, fim.getHours(), fim.getMinutes());
  return null;
}

const VAZIO = { frequencia: '' as const, ate: '', convidados: [] as Convidado[], rascunhoConvidado: null, local: '', meet: false };

/** Reunião nova: no horário clicado no calendário ou na próxima meia hora, com 1 hora de duração. */
export function estadoLivreNovo(inicio: Date | null = null, agora = new Date()): EstadoReuniaoLivre {
  const ini = inicio ?? new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), agora.getHours(), agora.getMinutes() < 30 ? 30 : 60);
  const fim = new Date(ini.getTime() + UMA_HORA_MS);
  const dia = diaLocal(ini);
  return { ...VAZIO, diaInteiro: false, dia, diaFinal: dia, horaInicio: horaLocal(ini), horaFim: horaLocal(fim), deslocFim: diasEntre(dia, diaLocal(fim)), duracaoMs: UMA_HORA_MS };
}

/** Primeiro e último dia (inclusivo) da reunião de dia inteiro, no fuso do workspace, em que o servidor gravou a meia-noite. */
export function diasDoDiaInteiro(a: Pick<Agendamento, 'inicio' | 'fim'>, fuso: string): { dia: string; diaFinal: string } {
  const dia = hojeNoFuso(fuso, new Date(a.inicio));
  const diaFinal = hojeNoFuso(fuso, new Date(new Date(a.fim).getTime() - 1));
  return { dia, diaFinal: diaFinal < dia ? dia : diaFinal };
}

/** Reunião gravada, para editar ou remarcar. Repetição não se edita: cada ocorrência da série é um agendamento. */
export function estadoLivreDaReuniao(a: Pick<Agendamento, 'convidados' | 'local' | 'reuniao_tipo' | 'dia_inteiro' | 'inicio' | 'fim'>, fuso: string): EstadoReuniaoLivre {
  const base = { ...VAZIO, convidados: a.convidados ?? [], local: a.local ?? '', meet: a.reuniao_tipo === 'google_meet' };
  if (a.dia_inteiro) {
    const { dia, diaFinal } = diasDoDiaInteiro(a, fuso);
    return { ...base, diaInteiro: true, dia, diaFinal, horaInicio: '09:00', horaFim: '10:00', deslocFim: 0, duracaoMs: UMA_HORA_MS };
  }
  const ini = new Date(a.inicio);
  const fim = new Date(a.fim);
  const dia = diaLocal(ini);
  return { ...base, diaInteiro: false, dia, diaFinal: dia, horaInicio: horaLocal(ini), horaFim: horaLocal(fim), deslocFim: diasEntre(dia, diaLocal(fim)), duracaoMs: fim.getTime() - ini.getTime() || UMA_HORA_MS };
}

/**
 * Mudança de horário. Mudar o início (dia ou hora) mantém a duração, como no Google; no dia inteiro, mantém quantos dias são.
 * Mudar o fim refaz a duração. Hora apagada no meio da edição não perde a duração guardada.
 */
export function mudarHorarioLivre(e: EstadoReuniaoLivre, d: Partial<Pick<EstadoReuniaoLivre, 'dia' | 'diaFinal' | 'horaInicio' | 'horaFim'>>): EstadoReuniaoLivre {
  const novo = { ...e, ...d };
  if (d.diaFinal !== undefined) return novo;
  if (d.dia !== undefined && ehDataIso(d.dia) && ehDataIso(e.dia) && ehDataIso(e.diaFinal)) novo.diaFinal = somarDiasIso(d.dia, Math.max(0, diasEntre(e.dia, e.diaFinal)));
  else if (d.dia !== undefined && ehDataIso(d.dia)) novo.diaFinal = d.dia;
  if (d.horaFim !== undefined) {
    novo.deslocFim = 0;
    const ini = inicioDoEstado(novo);
    const fim = fimDoEstado(novo);
    if (ini && fim) novo.duracaoMs = fim.getTime() - ini.getTime();
    return novo;
  }
  const ini = inicioDoEstado(novo);
  if (ini) {
    const fim = new Date(ini.getTime() + e.duracaoMs);
    novo.horaFim = horaLocal(fim);
    novo.deslocFim = diasEntre(novo.dia, diaLocal(fim));
  }
  return novo;
}

/** O horário para a API, ou nulo se ainda não vale. Mesma forma em criar, remarcar e na tarefa. */
export function horarioLivreParaApi(e: EstadoReuniaoLivre): { inicio: string; fim: string } | { dia_inteiro: true; dia_inicio: string; dia_fim: string } | null {
  if (e.diaInteiro) return ehDataIso(e.dia) && ehDataIso(e.diaFinal) && e.diaFinal >= e.dia ? { dia_inteiro: true, dia_inicio: e.dia, dia_fim: e.diaFinal } : null;
  const ini = inicioDoEstado(e);
  const fim = fimDoEstado(e);
  return ini && fim ? { inicio: ini.toISOString(), fim: fim.toISOString() } : null;
}

/**
 * Rodada rrl-6 (ROB6-M2): erro do servidor que nenhum campo da tela mostra precisa do aviso geral: sem campo nenhum, ou com um campo
 * fora dos que aparecem (`recurso_id`, do profissional desativado). Só os campos da tela ficam sem ele.
 */
export function temErroForaDaTela(porCampo: Record<string, string>, naTela: readonly string[]): boolean {
  const campos = Object.keys(porCampo);
  return !campos.length || campos.some((k) => !naTela.includes(k));
}

/** Convidados com o e-mail digitado e não incluído. */
export function convidadosDoEstado(e: Pick<EstadoReuniaoLivre, 'convidados' | 'rascunhoConvidado'>): Convidado[] {
  const r = e.rascunhoConvidado;
  if (!r || !RE_EMAIL.test(r.email) || e.convidados.some((c) => c.tipo !== 'interno' && c.email?.toLowerCase() === r.email.toLowerCase())) return e.convidados;
  return [...e.convidados, { tipo: 'externo', email: r.email, nome: r.nome || null }];
}

const tipoDaCaixa = (e: Pick<EstadoReuniaoLivre, 'meet'>): TipoReuniao => (e.meet ? 'google_meet' : 'nenhuma');

/**
 * Rodada rrl-2 (SPEC-M1): o que lista a reunião ligada ao parceiro. A ficha do contato e a da empresa no CRM (vínculos e o histórico
 * agregado deles) e a `FichaParceiro` de Cadastros; a ficha da negociação fica com a chave `pipeline`.
 */
export async function invalidarFichasDoParceiro(qc: QueryClient, parceiroId: string) {
  for (const chave of [['crm', 'contato', parceiroId, 'vinculos'], ['crm', 'contato', parceiroId, 'historico'], ['crm', 'empresa', parceiroId, 'vinculos'], ['crm', 'empresa', parceiroId, 'historico'], ['cadastros', 'vinculos', parceiroId]]) {
    await qc.invalidateQueries({ queryKey: chave });
  }
}

/** Corpo de criar (`POST /agenda/reunioes`) e de `reuniao_livre` da tarefa, sem título, descrição nem vínculo. */
export function reuniaoLivreParaApi(e: EstadoReuniaoLivre) {
  return {
    ...horarioLivreParaApi(e),
    ...(e.frequencia ? { recorrencia: { frequencia: e.frequencia, ate: e.ate } } : {}),
    local: e.local.trim() || null,
    reuniao_tipo: tipoDaCaixa(e),
    convidados: convidadosParaApi(convidadosDoEstado(e)),
  };
}

/** Corpo do editar: a caixa só troca o tipo quando mudou (reunião com link próprio não perde o link por estar desmarcada). */
export function edicaoLivreParaApi(e: EstadoReuniaoLivre, atual: Pick<Agendamento, 'reuniao_tipo'>) {
  const mudou = e.meet !== (atual.reuniao_tipo === 'google_meet');
  return { local: e.local.trim() || null, ...(mudou ? { reuniao_tipo: tipoDaCaixa(e) } : {}), convidados: convidadosParaApi(convidadosDoEstado(e)) };
}

/** Só título e horário são obrigatórios; "Até" passa a ser quando repete. O e-mail digitado precisa valer, porque vai junto. */
export function validarReuniaoLivre(e: EstadoReuniaoLivre, obrigatorio: string, t: (k: string) => string): Record<string, string> {
  const erros: Record<string, string> = {};
  if (e.diaInteiro) {
    if (!ehDataIso(e.dia)) erros.dia = obrigatorio;
    if (!ehDataIso(e.diaFinal)) erros.dia_fim = obrigatorio;
    else if (e.diaFinal < e.dia) erros.dia_fim = t('reuniaoLivre.erros.diaFinal');
  } else {
    if (!ehDataIso(e.dia)) erros.dia = obrigatorio;
    if (!RE_HORA.test(e.horaInicio)) erros.inicio = obrigatorio;
    if (!RE_HORA.test(e.horaFim)) erros.fim = obrigatorio;
    else if (!erros.dia && !erros.inicio && !fimDoEstado(e)) erros.fim = t('reuniaoLivre.erros.fim');
  }
  if (e.frequencia) {
    if (!ehDataIso(e.ate)) erros.ate = obrigatorio;
    else if (ehDataIso(e.dia) && e.ate < e.dia) erros.ate = t('reuniaoLivre.erros.ate');
  }
  if (e.rascunhoConvidado && !RE_EMAIL.test(e.rascunhoConvidado.email)) erros.convidados = t('convidados.emailInvalido');
  return erros;
}

export interface PreparoLivre {
  recurso_id: string | null;
  profissional_desativado: boolean;
  meet_disponivel: boolean;
  motivo_sem_meet: 'sem_conector' | 'sem_agenda' | null;
  convite_google: boolean;
}

/** O que o formulário precisa saber de quem pede. Carregando ou com erro, nada é impedido de salvar. */
export function usePreparoLivre(ativo = true) {
  const s = useSessao();
  return useQuery({ queryKey: [CHAVE, 'reuniao-livre', 'preparo'], queryFn: () => get<PreparoLivre>('/agenda/reunioes/preparo'), enabled: ativo && s.pode('agenda.criar'), staleTime: 30_000, retry: false });
}

/** Parâmetros da consulta de conflitos, ou nulo enquanto o horário não vale. */
export function paramsConflitosLivre(e: EstadoReuniaoLivre, ignorarId?: string): Record<string, string> | null {
  const h = horarioLivreParaApi(e);
  if (!h) return null;
  const horario: Record<string, string> = 'dia_inteiro' in h ? { dia_inteiro: 'true', dia_inicio: h.dia_inicio, dia_fim: h.dia_fim } : h;
  const params = { ...horario };
  if (e.frequencia && ehDataIso(e.ate) && e.ate >= e.dia) {
    params.frequencia = e.frequencia;
    params.ate = e.ate;
  }
  if (ignorarId) params.ignorar_id = ignorarId;
  return params;
}

/** Espera a digitação assentar antes de consultar. */
const ESPERA_CONFLITOS_MS = 400;

/**
 * Decisão 3 da ficha: como no Google, deixa marcar e só avisa, em texto e sem cor de erro. Só conta na agenda de quem pede (a
 * rota nunca diz o que há no horário). `ativo` falso (horário travado, reunião de outra pessoa): nada a dizer.
 */
export function AvisoConflitosLivre({ estado, ignorarId, ativo = true }: { estado: EstadoReuniaoLivre; ignorarId?: string; ativo?: boolean }) {
  const { t } = useTranslation('agenda');
  const s = useSessao();
  const chave = useMemo(() => JSON.stringify(ativo && s.pode('agenda.criar') ? paramsConflitosLivre(estado, ignorarId) : null), [ativo, s, estado, ignorarId]);
  const [adiada, setAdiada] = useState(chave);
  useEffect(() => {
    const m = setTimeout(() => setAdiada(chave), ESPERA_CONFLITOS_MS);
    return () => clearTimeout(m);
  }, [chave]);
  const params = JSON.parse(adiada) as Record<string, string> | null;
  const q = useQuery({
    queryKey: [CHAVE, 'reuniao-livre', 'conflitos', adiada],
    queryFn: () => get<{ conflitos: number; fora_do_horario: number }>('/agenda/reunioes/conflitos', params!),
    enabled: !!params,
    retry: false,
  });
  const serie = !!params?.frequencia;
  const avisos = [
    q.data?.conflitos ? (serie ? t('reuniaoLivre.aviso.conflitoSerie', { count: q.data.conflitos }) : t('reuniaoLivre.aviso.conflito')) : null,
    q.data?.fora_do_horario ? (serie ? t('reuniaoLivre.aviso.foraSerie', { count: q.data.fora_do_horario }) : t('reuniaoLivre.aviso.fora')) : null,
  ].filter(Boolean) as string[];
  // A região fica montada para o leitor de tela anunciar quando o aviso chega.
  return (
    <div role="status" className="min-w-0" data-aviso-conflito="">
      {!!params && q.isPending && <p className="mt-4 text-xs text-tinta-3">{t('reuniaoLivre.aviso.conferindo')}</p>}
      {!!params && q.isError && <p className="mt-4 text-xs text-tinta-3">{t('reuniaoLivre.aviso.erro')}</p>}
      {avisos.length > 0 && (
        <div className="mt-4 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-1">
          {avisos.map((a) => (
            <p key={a} className="min-w-0 break-words">
              {a}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** Data, início e fim (ou dia inteiro com data inicial e final). Também é o remarcar da reunião livre, no detalhe. */
export function CamposHorarioLivre({ idBase, estado, aoMudar, erros = {}, travado, ajudaTravado }: { idBase: string; estado: EstadoReuniaoLivre; aoMudar: (e: EstadoReuniaoLivre) => void; erros?: Record<string, string>; travado?: boolean; ajudaTravado?: string }) {
  const { t } = useTranslation('agenda');
  const fim = !estado.diaInteiro && fimDoEstado(estado);
  // O fim em outro dia aparece em texto: a grade só tem um campo de data.
  const ajudaFim = fim && estado.dia && diaLocal(fim) !== estado.dia ? t('reuniaoLivre.terminaEm', { data: fmtData(diaLocal(fim)) }) : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-horario-livre="">
      <CaixaMarcar id={`${idBase}-dia-inteiro`} rotulo={t('reuniaoLivre.diaInteiro')} marcado={estado.diaInteiro} desabilitado={travado} aoMudar={(v) => aoMudar({ ...estado, diaInteiro: v, diaFinal: v && (!ehDataIso(estado.diaFinal) || estado.diaFinal < estado.dia) ? estado.dia : estado.diaFinal })} />
      {estado.diaInteiro ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Rotulo id={`${idBase}-dia`} rotulo={t('reuniaoLivre.dataInicial')} obrigatorio erro={erros.dia ?? erros.inicio}>
            <CampoData id={`${idBase}-dia`} {...ariaErro(`${idBase}-dia`, erros.dia ?? erros.inicio)} name="dia-inicial" type="date" value={estado.dia} disabled={travado} onChange={(e) => aoMudar(mudarHorarioLivre(estado, { dia: e.target.value }))} />
          </Rotulo>
          <Rotulo id={`${idBase}-dia-fim`} rotulo={t('reuniaoLivre.dataFinal')} obrigatorio erro={erros.dia_fim ?? erros.fim}>
            <CampoData id={`${idBase}-dia-fim`} {...ariaErro(`${idBase}-dia-fim`, erros.dia_fim ?? erros.fim)} name="dia-final" type="date" min={estado.dia || undefined} value={estado.diaFinal} disabled={travado} onChange={(e) => aoMudar(mudarHorarioLivre(estado, { diaFinal: e.target.value }))} />
          </Rotulo>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div className="col-span-2 min-w-0 sm:col-span-1">
            <Rotulo id={`${idBase}-dia`} rotulo={t('campos.data')} obrigatorio erro={erros.dia}>
              <CampoData id={`${idBase}-dia`} {...ariaErro(`${idBase}-dia`, erros.dia)} name="dia" type="date" value={estado.dia} disabled={travado} onChange={(e) => aoMudar(mudarHorarioLivre(estado, { dia: e.target.value }))} />
            </Rotulo>
          </div>
          <Rotulo id={`${idBase}-inicio`} rotulo={t('campos.inicio')} obrigatorio erro={erros.inicio}>
            <CampoData id={`${idBase}-inicio`} {...ariaErro(`${idBase}-inicio`, erros.inicio)} name="hora-inicio" type="time" value={estado.horaInicio} disabled={travado} onChange={(e) => aoMudar(mudarHorarioLivre(estado, { horaInicio: e.target.value }))} />
          </Rotulo>
          <Rotulo id={`${idBase}-fim`} rotulo={t('campos.fim')} obrigatorio erro={erros.fim} ajuda={ajudaFim}>
            <CampoData id={`${idBase}-fim`} {...ariaErro(`${idBase}-fim`, erros.fim)} name="hora-fim" type="time" value={estado.horaFim} disabled={travado} aria-describedby={erros.fim ? `${idBase}-fim-erro` : ajudaFim ? `${idBase}-fim-ajuda` : undefined} onChange={(e) => aoMudar(mudarHorarioLivre(estado, { horaFim: e.target.value }))} />
          </Rotulo>
        </div>
      )}
      {travado && ajudaTravado && <p className="text-xs text-tinta-3">{ajudaTravado}</p>}
    </div>
  );
}

/** A caixa do Meet explica o estado; salvar nunca depende dela (a checagem de verdade é a do servidor). */
function CaixaMeet({ idBase, estado, aoMudar, erro, preparo, servidorDecide }: { idBase: string; estado: EstadoReuniaoLivre; aoMudar: (e: EstadoReuniaoLivre) => void; erro?: string; preparo: ReturnType<typeof usePreparoLivre>; servidorDecide?: boolean }) {
  const { t } = useTranslation('agenda');
  const disponivel = servidorDecide || preparo.data?.meet_disponivel === true;
  // Quem já está marcado pode sempre desmarcar (reunião gravada com Meet e conexão que saiu depois).
  const desabilitado = !disponivel && !estado.meet;
  const ajuda = disponivel
    ? estado.meet
      ? t('reuniaoLivre.meet.ajuda')
      : undefined
    : preparo.isError
      ? t('reuniaoLivre.meet.erro')
      : preparo.isPending
        ? t('reuniaoLivre.meet.verificando')
        : (
            <>
              {t('reuniaoLivre.meet.semGoogle')}{' '}
              {/* Rodada rrl-1 (VIS-B5): em nova aba, para não perder o que já foi preenchido. */}
              <Link to="/conta" target="_blank" rel="noopener noreferrer" className="font-medium text-acento-forte underline" data-conectar-google="">
                {t('reuniaoLivre.meet.caminhoConta')}
              </Link>
              .
            </>
          );
  return (
    <div className="flex min-w-0 flex-col gap-1" data-caixa-meet="">
      <CaixaMarcar id={`${idBase}-meet`} rotulo={t('reuniaoLivre.meet.adicionar')} ajuda={ajuda} marcado={estado.meet} desabilitado={desabilitado} aoMudar={(v) => aoMudar({ ...estado, meet: v })} />
      {/* `role="alert"`: a caixa não é campo de texto; o foco da validação (`focarErro`) vem para a mensagem. */}
      {erro && (
        <p role="alert" className="text-xs text-perigo">
          {erro}
        </p>
      )}
    </div>
  );
}

/**
 * Os campos do formulário livre. `conviteGoogle` e `servidorDecideMeet` são da edição: quem convida é a reunião gravada, e o
 * preparo só fala de quem pede (na reunião de outra pessoa, o servidor confere o Meet do profissional dela).
 */
export function CamposReuniaoLivre({
  idBase,
  estado,
  aoMudar,
  erros = {},
  preparo,
  nomes = [],
  semRepeticao,
  travaHorario,
  ajudaTravado,
  ignorarId,
  avisarConflitos = true,
  conviteGoogle,
  servidorDecideMeet,
}: {
  idBase: string;
  estado: EstadoReuniaoLivre;
  aoMudar: (e: EstadoReuniaoLivre) => void;
  erros?: Record<string, string>;
  preparo: ReturnType<typeof usePreparoLivre>;
  nomes?: (string | null | undefined)[];
  semRepeticao?: boolean;
  travaHorario?: boolean;
  ajudaTravado?: string;
  ignorarId?: string;
  avisarConflitos?: boolean;
  conviteGoogle?: boolean;
  servidorDecideMeet?: boolean;
}) {
  const { t } = useTranslation('agenda');
  const convite = conviteGoogle ?? preparo.data?.convite_google;
  const repete = !semRepeticao && !!estado.frequencia;
  return (
    <div className="flex min-w-0 flex-col gap-4" data-campos-reuniao-livre="">
      {/* Rodada rrl-1 (VIS-B1): um grupo só; a região do aviso fica montada sem somar um vão quando vazia (o espaço é do conteúdo dela). */}
      <div className="min-w-0">
        <CamposHorarioLivre idBase={idBase} estado={estado} aoMudar={aoMudar} erros={erros} travado={travaHorario} ajudaTravado={ajudaTravado} />
        {!semRepeticao && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Rotulo id={`${idBase}-repeticao`} rotulo={t('campos.recorrencia')}>
              <Selecao
                id={`${idBase}-repeticao`}
                name="repeticao"
                value={estado.frequencia}
                opcoes={[{ valor: '', rotulo: t('reuniaoLivre.repeticao.nenhuma') }, ...REPETICOES.map((f) => ({ valor: f, rotulo: t(`reuniaoLivre.repeticao.${f}`) }))]}
                onChange={(e) => aoMudar({ ...estado, frequencia: e.target.value as Frequencia | '' })}
              />
            </Rotulo>
            {repete && (
              <Rotulo id={`${idBase}-ate`} rotulo={t('reuniaoLivre.ate')} obrigatorio erro={erros.ate ?? erros.recorrencia}>
                <CampoData id={`${idBase}-ate`} {...ariaErro(`${idBase}-ate`, erros.ate ?? erros.recorrencia)} name="ate" type="date" min={estado.dia || undefined} value={estado.ate} onChange={(e) => aoMudar({ ...estado, ate: e.target.value })} />
              </Rotulo>
            )}
          </div>
        )}
        {avisarConflitos && <AvisoConflitosLivre estado={estado} ignorarId={ignorarId} ativo={!travaHorario} />}
      </div>
      <div className="flex min-w-0 flex-col gap-2" data-convidados-livre="">
        <CampoConvidados idBase={`${idBase}-conv`} valor={estado.convidados} aoMudar={(convidados) => aoMudar({ ...estado, convidados })} aoRascunho={(r) => (r?.email !== estado.rascunhoConvidado?.email || r?.nome !== estado.rascunhoConvidado?.nome) && aoMudar({ ...estado, rascunhoConvidado: r })} erroEmail={estado.rascunhoConvidado ? erros.convidados : undefined} />
        {erros.convidados && !estado.rascunhoConvidado && (
          <p role="alert" className="text-xs text-perigo">
            {erros.convidados}
          </p>
        )}
        {convite !== undefined && (
          <p className="text-xs text-tinta-3" data-ajuda-convite="">
            {t(convite ? 'reuniaoLivre.convite.google' : 'reuniaoLivre.convite.email')}
            {convite && repete ? ` ${t('reuniaoLivre.convite.repeticao')}` : ''}
          </p>
        )}
      </div>
      <Rotulo id={`${idBase}-local`} rotulo={t('campos.local')} erro={erros.local}>
        <EntradaSensivel id={`${idBase}-local`} {...ariaErro(`${idBase}-local`, erros.local)} name="local" mascara={null} livre nomesNoTexto={nomes} maxLength={300} value={estado.local} onChange={(e) => aoMudar({ ...estado, local: e.target.value })} />
      </Rotulo>
      <CaixaMeet idBase={idBase} estado={estado} aoMudar={aoMudar} erro={erros.reuniao_tipo} preparo={preparo} servidorDecide={servidorDecideMeet} />
    </div>
  );
}

/** Erro do profissional desativado: o mesmo texto no topo do formulário e na descrição do botão de salvar. */
export function AvisoProfissionalDesativado({ id }: { id: string }) {
  const { t } = useTranslation('agenda');
  return (
    <p id={id} role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-1" data-profissional-desativado="">
      {t('reuniaoLivre.profissionalDesativado')}
    </p>
  );
}
